/**
 * Llamadas HTTP del dominio Talonarios.
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials:
 * 'include'`, el `X-CSRF-TOKEN` y el `X-Sede-Id` (sede activa) en cada request,
 * así que estos listados/acciones operan sobre la sede activa sin tocar las
 * cabeceras a mano. Un `ApiError` (401/403/404/4xx/5xx) lo traduce el cliente y
 * lo gestionan los hooks/pantallas.
 *
 * Cubre toda la superficie de la API de talonarios (#50): estado/grid (actual e
 * histórico), historial paginado, descuadres, alertas y las escrituras
 * (asignar/marcar/desmarcar/marcar alertas vistas).
 */
import { api } from "@/lib/api";

import type {
  AssignTalonarioBody,
  MarkNumberBody,
  TalonarioAlertasResponse,
  TalonarioGrid,
  TalonarioHistoryPage,
  TalonarioHistoryQuery,
  TalonarioMismatchesQuery,
  TalonarioMismatchesResponse,
  TalonarioStatus,
  TalonariosList,
} from "./types";

/** Codifica un nombre de trabajador para usarlo como segmento de ruta. */
function encodeWorker(worker: string): string {
  return encodeURIComponent(worker);
}

/**
 * Estado de todos los talonarios de la sede (`GET /api/v1/talonarios`).
 *
 * Permiso backend: `manage_talonarios`. Devuelve `{items}` con el estado de cada
 * trabajador con configuración en la sede activa.
 */
export async function fetchTalonarios(): Promise<TalonariosList> {
  const response = await api.get("talonarios");
  return (await response.json()) as TalonariosList;
}

/**
 * Estado del talonario de un trabajador (`GET /api/v1/talonarios/<worker>`).
 *
 * Permiso backend: `manage_talonarios`. 404 si el trabajador no tiene talonario
 * en la sede activa.
 */
export async function fetchTalonarioStatus(worker: string): Promise<TalonarioStatus> {
  const response = await api.get(`talonarios/${encodeWorker(worker)}`);
  return (await response.json()) as TalonarioStatus;
}

/**
 * Grid del talonario ACTUAL de un trabajador (`GET /api/v1/talonarios/<worker>/grid`).
 *
 * Permiso backend: `manage_talonarios`. Por cada número del rango vigente trae su
 * estado (`order`/`marked`/`available`) y contenido.
 */
export async function fetchTalonarioGrid(worker: string): Promise<TalonarioGrid> {
  const response = await api.get(`talonarios/${encodeWorker(worker)}/grid`);
  return (await response.json()) as TalonarioGrid;
}

/**
 * Grid de un rango HISTÓRICO de talonario
 * (`GET /api/v1/talonarios/<worker>/<start>/<end>`).
 *
 * Permiso backend: `manage_talonarios`. Solo lectura: el rango ya no está en uso.
 */
export async function fetchTalonarioRangeGrid(
  worker: string,
  start: number,
  end: number,
): Promise<TalonarioGrid> {
  const response = await api.get(`talonarios/${encodeWorker(worker)}/${start}/${end}`);
  return (await response.json()) as TalonarioGrid;
}

/** Construye el query string del historial desde los filtros. */
function buildHistorySearchParams(query: TalonarioHistoryQuery): URLSearchParams {
  return new URLSearchParams({
    worker: query.worker,
    action: query.action,
    page: String(query.page),
    per_page: String(query.per_page),
  });
}

/**
 * Historial paginado de operaciones (`GET /api/v1/talonarios/history`).
 *
 * Permiso backend: `manage_talonarios`. Filtros parciales `worker`/`action` +
 * paginación (`page`/`per_page`).
 */
export async function fetchTalonarioHistory(
  query: TalonarioHistoryQuery,
): Promise<TalonarioHistoryPage> {
  const response = await api.get("talonarios/history", {
    searchParams: buildHistorySearchParams(query),
  });
  return (await response.json()) as TalonarioHistoryPage;
}

/** Construye el query string de descuadres desde los filtros. */
function buildMismatchesSearchParams(query: TalonarioMismatchesQuery): URLSearchParams {
  return new URLSearchParams({
    start_date: query.start_date,
    end_date: query.end_date,
    worker: query.worker,
  });
}

/**
 * Descuadres: órdenes con atendido ≠ dueño del rango
 * (`GET /api/v1/talonarios/mismatches`).
 *
 * Permiso backend: `manage_talonarios`. Filtros `start_date`/`end_date`
 * (`DD/MM/AAAA` o `AAAA-MM-DD`) y `worker`. Devuelve `{items, workers}` (los
 * descuadres + los atendidos presentes, para poblar el filtro).
 */
export async function fetchTalonarioMismatches(
  query: TalonarioMismatchesQuery,
): Promise<TalonarioMismatchesResponse> {
  const response = await api.get("talonarios/mismatches", {
    searchParams: buildMismatchesSearchParams(query),
  });
  return (await response.json()) as TalonarioMismatchesResponse;
}

/**
 * Alertas de salto no vistas (`GET /api/v1/talonarios/alertas`).
 *
 * Permiso backend: `manage_talonarios` **silencioso** (sin permiso devuelve
 * `{count: 0, alertas: []}` en vez de 403). Las consume el panel del supervisor.
 */
export async function fetchTalonarioAlertas(): Promise<TalonarioAlertasResponse> {
  const response = await api.get("talonarios/alertas");
  return (await response.json()) as TalonarioAlertasResponse;
}

/**
 * Asigna un nuevo rango a un trabajador (`POST /api/v1/talonarios/assign`).
 *
 * Permiso backend: `manage_talonarios`. El rango debe tener exactamente 50
 * números (la UI lo valida con Zod). Devuelve el estado actualizado del
 * trabajador (201). Los errores (`{error_code, message}`) los muestra el modal.
 */
export async function assignTalonario(
  body: AssignTalonarioBody,
): Promise<TalonarioStatus> {
  const response = await api.post("talonarios/assign", { json: body });
  return (await response.json()) as TalonarioStatus;
}

/** Resultado de marcar/desmarcar: el número y su nuevo estado de marca. */
export interface MarkResult {
  worker: string;
  number: number;
  marked: boolean;
}

/**
 * Marca un número del talonario como ocupado (`POST /api/v1/talonarios/mark`).
 *
 * Permiso backend: `manage_talonarios`. Devuelve `{worker, number, marked: true}`
 * (201). Errores típicos: `number_out_of_range`, `number_in_use`, `already_marked`.
 */
export async function markTalonarioNumber(body: MarkNumberBody): Promise<MarkResult> {
  const response = await api.post("talonarios/mark", { json: body });
  return (await response.json()) as MarkResult;
}

/**
 * Desmarca un número del talonario (`POST /api/v1/talonarios/unmark`).
 *
 * Permiso backend: `manage_talonarios`. Devuelve `{worker, number, marked: false}`
 * (200). 404 si el número no estaba marcado.
 */
export async function unmarkTalonarioNumber(body: MarkNumberBody): Promise<MarkResult> {
  const response = await api.post("talonarios/unmark", { json: body });
  return (await response.json()) as MarkResult;
}
